"use client";

import React from "react";
import { useRouter } from "next/navigation";
import {
  Lock,
  Users,
  Lightbulb,
  BookOpen,
  UserPlus,
  BarChart3,
  CheckCircle,
  Quote,
} from "lucide-react";

// -----------------------------------------------------------------------------
// Minimal UI primitives (no external deps)
// -----------------------------------------------------------------------------
function Button({
  children,
  className = "",
  onClick,
  variant = "primary",
  size = "md",
  mobileSize,
}: React.PropsWithChildren<{
  className?: string;
  onClick?: () => void;
  variant?: "primary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  mobileSize?: "sm" | "md" | "lg";
}>) {
  const base =
    "inline-flex items-center justify-center rounded-xl font-medium transition-colors select-none";
  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-base",
  } as const;
  const variants = {
    primary: "bg-indigo-600 text-white hover:bg-indigo-700",
    outline: "border border-slate-300 text-slate-700 hover:bg-slate-50",
    ghost: "text-slate-700 hover:bg-slate-100",
  } as const;

  const getResponsiveSizeClasses = (
    desktopSize: "sm" | "md" | "lg",
    mobileSizeValue?: "sm" | "md" | "lg"
  ): string => {
    if (!mobileSizeValue) {
      return sizes[desktopSize];
    }

    const mobileClasses = sizes[mobileSizeValue];
    const desktopClasses = sizes[desktopSize];

    const mobileParts = mobileClasses.split(" ");
    const desktopParts = desktopClasses.split(" ");

    const responsiveClasses: string[] = [];

    mobileParts.forEach((mobileClass) => {
      if (mobileClass.startsWith("px-")) {
        responsiveClasses.push(mobileClass);
        const desktopPx = desktopParts.find((c) => c.startsWith("px-"));
        if (desktopPx) {
          responsiveClasses.push(`md:${desktopPx}`);
        }
      } else if (mobileClass.startsWith("py-")) {
        responsiveClasses.push(mobileClass);
        const desktopPy = desktopParts.find((c) => c.startsWith("py-"));
        if (desktopPy) {
          responsiveClasses.push(`md:${desktopPy}`);
        }
      } else if (mobileClass.startsWith("text-")) {
        responsiveClasses.push(mobileClass);
        const desktopText = desktopParts.find((c) => c.startsWith("text-"));
        if (desktopText) {
          responsiveClasses.push(`md:${desktopText}`);
        }
      }
    });

    return responsiveClasses.join(" ");
  };

  const sizeClasses = getResponsiveSizeClasses(size, mobileSize);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${sizeClasses} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
}
function Card({
  className = "",
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 shadow-sm bg-white ${className}`}
    >
      {children}
    </div>
  );
}
function CardHeader({
  className = "",
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  return <div className={`px-5 pt-5 ${className}`}>{children}</div>;
}
function CardTitle({
  className = "",
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  return <h3 className={`text-lg font-semibold ${className}`}>{children}</h3>;
}
function CardContent({
  className = "",
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  return <div className={`px-5 pb-6 ${className}`}>{children}</div>;
}

// lucide-react 아이콘을 사용하므로 인라인 아이콘 컴포넌트는 제거됨

// -----------------------------------------------------------------------------
// Data (defined BEFORE render)
// -----------------------------------------------------------------------------
const aboutCards = [
  {
    icon: "Users",
    t: `이런 분들께 필요합니다`,
    d: `커리어 전환을 준비 중인 분, 내 시장 가치를 점검하고 싶은 분, 불확실한 미래가 불안하신 분을 위해 설계되었습니다.`,
  },
  {
    icon: "Lightbulb",
    t: `무엇을 진단하나요?`,
    d: `장기 잠재력(행동 역량)과 즉시 경쟁력(시장 스킬), 두 축을 종합 진단하여 균형 잡힌 성장 방향을 제시합니다.`,
  },
  {
    icon: "BookOpen",
    t: `무엇을 받게 되나요?`,
    d: `세부 역량 점수, 강점/약점 분석, 시각화 프로파일까지\n당신의 고용가능성을 한 권의 리포트로 제공합니다.`,
  },
] as const;

const processSteps = [
  {
    t: `1단계: 시작 및 나의 유형 선택`,
    d: `로그인 후, 부여받은 진단코드를 입력하고,\n나의 현재 커리어 상황에 해당하는 유형을 선택합니다.`,
    icon: "UserPlus",
  },
  {
    t: `2단계: 맞춤 진단 참여`,
    d: `유형별 최적화 문항으로 행동 역량과 시장 스킬을 측정합니다. (약 10분 ~ 15분 소요)`,
    icon: "BarChart3",
  },
  {
    t: `3단계: 통합 리포트 확인`,
    d: `진단 완료 후, 개별 점수, 프로파일,\n강점/약점 분석, 맞춤 성장 전략을 즉시 확인하세요.`,
    icon: "CheckCircle",
  },
] as const;

// -----------------------------------------------------------------------------
// SVG previews (always visible; later blurred in teaser)
// -----------------------------------------------------------------------------
const GaugePreview = () => (
  <svg viewBox="0 0 240 180" className="w-full h-full">
    <defs>
      <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#00A89D" />
        <stop offset="100%" stopColor="#B6E03C" />
      </linearGradient>
    </defs>
    <path
      d="M 40 140 A 80 80 0 0 1 200 140"
      fill="none"
      stroke="#E0E0E0"
      strokeWidth="20"
      strokeLinecap="round"
    />
    <path
      d="M 40 140 A 80 80 0 0 1 180 70"
      fill="none"
      stroke="url(#gaugeGradient)"
      strokeWidth="20"
      strokeLinecap="round"
    />
    <text
      x="120"
      y="130"
      textAnchor="middle"
      fontSize="48"
      fontWeight="700"
      fill="#00A89D"
    >
      82
    </text>
    <text
      x="120"
      y="155"
      textAnchor="middle"
      fontSize="14"
      fill="#666"
      fontWeight="500"
    >
      종합 점수
    </text>
    <g>
      <rect
        x="15"
        y="15"
        width="90"
        height="45"
        rx="6"
        fill="#f0f8ff"
        stroke="#00A89D"
        strokeWidth="1.5"
      />
      <text
        x="60"
        y="32"
        textAnchor="middle"
        fontSize="11"
        fill="#666"
        fontWeight="500"
      >
        행동 역량
      </text>
      <text
        x="60"
        y="50"
        textAnchor="middle"
        fontSize="20"
        fill="#00A89D"
        fontWeight="700"
      >
        4.1
      </text>
    </g>
    <g>
      <rect
        x="135"
        y="15"
        width="90"
        height="45"
        rx="6"
        fill="#f0fff0"
        stroke="#B6E03C"
        strokeWidth="1.5"
      />
      <text
        x="180"
        y="32"
        textAnchor="middle"
        fontSize="11"
        fill="#666"
        fontWeight="500"
      >
        시장 스킬
      </text>
      <text
        x="180"
        y="50"
        textAnchor="middle"
        fontSize="20"
        fill="#5a8a00"
        fontWeight="700"
      >
        3.9
      </text>
    </g>
  </svg>
);

const RadarPreview = () => {
  const competencies = [
    { label: "학습민첩성", value: 84, angle: 0 },
    { label: "문제해결력", value: 76, angle: 45 },
    { label: "주도성", value: 90, angle: 90 },
    { label: "회복탄력성", value: 72, angle: 135 },
    { label: "디지털", value: 86, angle: 180 },
    { label: "전략적사고", value: 78, angle: 225 },
    { label: "협업", value: 74, angle: 270 },
    { label: "경력주도성", value: 80, angle: 315 },
  ];
  const centerX = 120,
    centerY = 100,
    maxRadius = 70;
  const getPoint = (angle: number, value: number) => {
    const rad = ((angle - 90) * Math.PI) / 180;
    const radius = (value / 100) * maxRadius;
    return {
      x: centerX + radius * Math.cos(rad),
      y: centerY + radius * Math.sin(rad),
    };
  };
  const points = competencies.map((c) => getPoint(c.angle, c.value));
  const polygonPoints = points.map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <svg viewBox="0 0 240 200" className="w-full h-full">
      <defs>
        <linearGradient id="radarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00A89D" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#B6E03C" stopOpacity="0.3" />
        </linearGradient>
      </defs>
      {[20, 40, 60].map((radius, i) => (
        <circle
          key={i}
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="1"
        />
      ))}
      {competencies.map((comp, i) => {
        const end = getPoint(comp.angle, 100);
        return (
          <line
            key={i}
            x1={centerX}
            y1={centerY}
            x2={end.x}
            y2={end.y}
            stroke="#cbd5e1"
            strokeWidth="1"
          />
        );
      })}
      <polygon
        points={polygonPoints}
        fill="url(#radarGradient)"
        stroke="#00A89D"
        strokeWidth="2"
      />
      {points.map((point, i) => (
        <circle
          key={i}
          cx={point.x}
          cy={point.y}
          r="3"
          fill="#00A89D"
          stroke="white"
          strokeWidth="1.5"
        />
      ))}
      <text x="120" y="15" textAnchor="middle" fontSize="9" fill="#666">
        학습민첩성
      </text>
      <text x="200" y="100" textAnchor="start" fontSize="9" fill="#666">
        디지털
      </text>
      <text x="40" y="100" textAnchor="end" fontSize="9" fill="#666">
        협업
      </text>
      <text
        x="120"
        y="190"
        textAnchor="middle"
        fontSize="11"
        fill="#666"
        fontWeight="500"
      >
        8개 역량 상세 분석
      </text>
    </svg>
  );
};

const QuadrantPreview = () => {
  const centerX = 120,
    centerY = 100;
  const pointX = centerX + (4.1 - 2.5) * 30;
  const pointY = centerY - (3.9 - 2.5) * 30;
  return (
    <svg viewBox="0 0 240 200" className="w-full h-full">
      <defs>
        <linearGradient id="q1Gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#B6E03C" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#00A89D" stopOpacity="0.15" />
        </linearGradient>
      </defs>
      <rect x="10" y="10" width="220" height="180" rx="8" fill="#f8fafc" />
      <rect
        x="10"
        y="10"
        width="110"
        height="90"
        fill="url(#q1Gradient)"
        opacity="0.8"
      />
      <rect
        x="120"
        y="10"
        width="110"
        height="90"
        fill="#f0f0f0"
        opacity="0.3"
      />
      <rect
        x="10"
        y="100"
        width="110"
        height="90"
        fill="#f0f0f0"
        opacity="0.3"
      />
      <rect
        x="120"
        y="100"
        width="110"
        height="90"
        fill="#f0f0f0"
        opacity="0.3"
      />
      <line
        x1={centerX}
        y1="15"
        x2={centerX}
        y2="185"
        stroke="#94a3b8"
        strokeWidth="2"
        strokeDasharray="4 4"
      />
      <line
        x1="15"
        y1={centerY}
        x2="225"
        y2={centerY}
        stroke="#94a3b8"
        strokeWidth="2"
        strokeDasharray="4 4"
      />
      <circle
        cx={pointX}
        cy={pointY}
        r="8"
        fill="#00A89D"
        stroke="white"
        strokeWidth="3"
      />
    </svg>
  );
};

const TeaserOverlay = ({
  label = "진단 완료 후 열람 가능",
}: {
  label?: string;
}) => (
  <div
    data-teaser
    className="pointer-events-none absolute inset-0 flex flex-col items-center justify-end p-3"
    style={{ bottom: 0 }}
  >
    <div className="mb-2 text-[10px] uppercase tracking-wider text-slate-500">
      샘플 미리보기
    </div>
    <div className="rounded-full bg-slate-900/80 text-white text-xs px-3 py-1.5 flex items-center gap-1">
      <Lock className="w-3 h-3" />
      {label}
    </div>
  </div>
);

const sampleCards = [
  {
    t: `나의 고용가능성 점수`,
    d: `행동 역량과 시장 스킬, 그리고 종합 점수를 한눈에 확인`,
    comp: <GaugePreview />,
  },
  {
    t: `역량별 상세 분석`,
    d: `8개 핵심 역량의 강점과 보완 영역 시각화`,
    comp: <RadarPreview />,
  },
  {
    t: `나의 현재 위치 확인`,
    d: `4사분면 매트릭스에서 나의 위치와 방향성 미리보기`,
    comp: <QuadrantPreview />,
  },
] as const;

const testimonials = [
  {
    quote: `"불안했던 미래가 이제 기대로 바뀌었어요"`,
    body: `불안정한 시대에 '나는 괜찮을까?' 고민이 많았는데, 진단을 통해 제가 어떤 환경에서도 일할 수 있는 역량을 가졌다는 걸 확인할 수 있었습니다. 변화가 두렵지 않게 되었어요.`,
    author: `— 5년차 기획자, 김ㅇㅇ님`,
  },
  {
    quote: `"당장의 스킬뿐 아니라, 장기적으로 성장할 수 있는 힘을 발견했어요"`,
    body: `지금 가진 기술이 언젠가 쓸모없어질까 봐 불안했는데, 진단 결과를 보고 제게 학습민첩성과 적응력이 있다는 걸 알게 되었습니다. 미래가 조금 덜 불안해졌어요.`,
    author: `— 8년차 개발자, 신ㅇㅇ님`,
  },
  {
    quote: `"조직에 의존하지 않고, 내 힘으로 커리어를 설계할 수 있게 되었어요"`,
    body: `회사가 어떻게 될지 모르는 상황에서 '나는 어디로 가야 하나' 막막했는데, 진단을 통해 제가 가진 강점과 시장에서 필요한 역량을 모두 파악할 수 있었습니다. 이제 제 커리어는 제가 주도합니다.`,
    author: `— 12년차 리더, 강ㅇㅇ님`,
  },
] as const;

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
export default function Home() {
  const router = useRouter();

  const handleStart = () => {
    router.push("/assessment");
  };

  const handleLearnMore = () => {
    document.getElementById("how")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Hero */}
      <section
        id="intro"
        className="relative overflow-hidden bg-gradient-to-b from-blue-50 to-white"
      >
        <div className="mx-auto max-w-7xl px-4 py-24 text-center">
          <h1 className="text-3xl md:text-6xl font-extrabold leading-tight text-slate-900">
            나의{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-500 bg-clip-text text-transparent">
              고용가능성
            </span>
            을 진단하고,
            <br /> 어떤 변화에도 흔들리지 않는 커리어를 만드세요.
          </h1>
          <p className="mt-6 text-slate-700 text-xl md:text-2xl max-w-3xl mx-auto font-medium">
            변화 속에서도 살아남는 장기 잠재력과 즉시 경쟁력,
            <br /> 두 가지를 동시에 진단하고 나의 강화 포인트를 발견하세요.
          </p>
          <div className="mt-10 flex justify-center gap-4">
            <Button
              size="lg"
              mobileSize="md"
              className="gap-2 text-lg px-8 py-4"
              onClick={handleStart}
            >
              진단 시작하기
            </Button>
            <Button
              size="lg"
              mobileSize="md"
              variant="outline"
              className="gap-2 text-lg px-8 py-4"
              onClick={handleLearnMore}
            >
              진행 방식 보기
            </Button>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-24 bg-white">
        <div className="mx-auto max-w-[1600px] px-4 text-center">
          <h2 className="text-[2.0625rem] md:text-[2.475rem] font-bold text-slate-900">
            고용가능성 진단이란?
          </h2>
          <p className="mt-6 text-slate-700 text-[1.2375rem] md:text-[1.375rem] max-w-3xl mx-auto leading-relaxed">
            <b className="text-slate-900">고용가능성(Employability)</b>은 단순한
            취업 가능성을 넘어, 원하는 일을 선택하고,
            <br />
            변화 속에서도 커리어를 유지하며, 필요시 새로운 형태로 전환할 수 있는{" "}
            <span className="whitespace-nowrap">총체적 역량입니다.</span>
            <br />
            이 진단은 장기 잠재력과 즉시 경쟁력 두 축을 측정하여,
            <br />
            당신의 강점과 성장 방향을 명확히 제시합니다.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {aboutCards.map((k, idx) => {
              const IconComponent =
                k.icon === "Users"
                  ? Users
                  : k.icon === "Lightbulb"
                    ? Lightbulb
                    : BookOpen;
              return (
                <Card
                  key={idx}
                  className="whitespace-pre-line hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <CardHeader className="flex flex-col items-center text-center">
                    <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 grid place-items-center text-white mb-4">
                      <IconComponent className="w-8 h-8" />
                    </div>
                    <CardTitle className="text-[1.375rem] md:text-[1.65rem] font-bold text-slate-900">
                      {k.t}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-center text-[1.1rem] md:text-[1.2375rem] text-slate-700 leading-relaxed">
                    {k.d}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how" className="py-24 bg-slate-50">
        <div className="mx-auto max-w-[1600px] px-4">
          <h2 className="text-[2.0625rem] md:text-[2.475rem] font-bold text-center text-slate-900">
            맞춤형 진단 프로세스
          </h2>
          <p className="mt-4 text-center text-slate-700 text-[1.375rem] md:text-[1.65rem] font-medium">
            "당신의 커리어 상황에 최적화된 질문으로 더 정확하게"
          </p>
          <p className="mt-6 max-w-3xl mx-auto text-center text-slate-700 text-[1.1rem] md:text-[1.2375rem] leading-relaxed">
            커리어 상황마다 필요한 역량과 직면한 고민은 다릅니다.
            <br />
            <b className="text-slate-900">유형별 최적화 문항</b>으로 행동 역량과
            시장 스킬을 측정합니다.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {processSteps.map((s, idx) => {
              const IconComponent =
                s.icon === "UserPlus"
                  ? UserPlus
                  : s.icon === "BarChart3"
                    ? BarChart3
                    : CheckCircle;
              return (
                <Card
                  key={idx}
                  className="text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <CardHeader>
                    <div className="flex justify-center mb-4">
                      <IconComponent className="w-8 h-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-[1.375rem] md:text-[1.65rem] font-bold text-slate-900">
                      {s.t}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-slate-700 text-[1.1rem] md:text-[1.2375rem] whitespace-pre-line leading-relaxed">
                    {s.d}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sample Report (teaser) */}
      <section id="sample" className="py-24 bg-white">
        <div className="mx-auto max-w-[1600px] px-4 text-center">
          <h2 className="text-[2.0625rem] md:text-[2.475rem] font-bold text-slate-900">
            진단 결과 미리보기
          </h2>
          <p className="mt-4 text-slate-700 text-[1.2375rem] md:text-[1.375rem]">
            실제 리포트에서 어떤 인사이트를 받게 되는지 확인해보세요
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-6 text-left">
            {sampleCards.map((card, idx) => (
              <Card
                key={idx}
                className="hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <CardHeader>
                  <CardTitle className="text-[1.375rem] md:text-[1.65rem] font-bold text-slate-900">
                    {card.t}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-slate-700 text-[1.1rem] md:text-[1.2375rem]">
                  <p className="leading-relaxed">{card.d}</p>
                  <div className="relative h-40 md:h-48 lg:h-56 mt-5 rounded-xl bg-slate-50 overflow-hidden shadow-inner">
                    <TeaserOverlay label="개인리포트 상세 내용 확인 가능" />
                    <div className="w-full h-full filter blur-[3px] opacity-20 pointer-events-none grid place-items-center">
                      <div className="w-full h-full">{card.comp}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section id="reviews" className="py-24 bg-slate-50">
        <div className="mx-auto max-w-[1600px] px-4 text-center">
          <h2 className="text-[2.0625rem] md:text-[2.475rem] font-bold text-slate-900">
            진단 참여자들의 이야기
          </h2>
          <p className="mt-4 text-slate-700 text-[1.375rem] md:text-[1.65rem] font-medium">
            "이제 변화가 두렵지 않아요"
          </p>
          <div className="mt-10 grid gap-8 md:grid-cols-3 text-left">
            {testimonials.map((t, idx) => (
              <Card
                key={idx}
                className="flex flex-col justify-between hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <CardHeader>
                  <div className="mb-3">
                    <Quote className="w-6 h-6 text-blue-500" />
                  </div>
                  <CardTitle className="text-[1.2375rem] leading-relaxed text-slate-900 whitespace-pre-line font-semibold">
                    {t.quote}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-slate-600 text-[1.1rem] whitespace-pre-line">
                  <p className="mb-4 leading-relaxed">{t.body}</p>
                  <p className="text-slate-500 text-[0.9625rem] font-medium">
                    {t.author}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
